import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, ArrowRightLeft, ChevronDown, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParticipantsTable = ({ draw, participants, onClose, onRefresh }) => {
  const [availableDraws, setAvailableDraws] = useState([]);
  const [selectedTargetDraw, setSelectedTargetDraw] = useState({});
  const [transferring, setTransferring] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState({}); // { odajsdjasd-participant-user-id: ['0001', '0002'] }

  // Fetch available draws for transfer
  useEffect(() => {
    const fetchDraws = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/admin/draws?include_archived=false`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Filter out current draw and completed draws
        const otherDraws = response.data.filter(d => 
          d.id !== draw.id && d.status !== 'completed'
        );
        setAvailableDraws(otherDraws);
      } catch (error) {
        console.error('Error fetching draws:', error);
      }
    };
    fetchDraws();
  }, [draw.id]);

  // Toggle ticket selection
  const toggleTicketSelection = (userId, ticketNumber) => {
    setSelectedTickets(prev => {
      const userTickets = prev[userId] || [];
      if (userTickets.includes(ticketNumber)) {
        // Remove ticket
        return { ...prev, [userId]: userTickets.filter(t => t !== ticketNumber) };
      } else {
        // Add ticket
        return { ...prev, [userId]: [...userTickets, ticketNumber] };
      }
    });
  };

  // Select all tickets for a user
  const selectAllTickets = (userId, allTicketNumbers) => {
    const currentSelected = selectedTickets[userId] || [];
    if (currentSelected.length === allTicketNumbers.length) {
      // Deselect all
      setSelectedTickets(prev => ({ ...prev, [userId]: [] }));
    } else {
      // Select all
      setSelectedTickets(prev => ({ ...prev, [userId]: [...allTicketNumbers] }));
    }
  };

  const handleTransfer = async (participant) => {
    const targetDrawId = selectedTargetDraw[participant.user_id];
    if (!targetDrawId) {
      toast.error('Please select a target draw first');
      return;
    }

    const ticketsToTransfer = selectedTickets[participant.user_id] || [];
    if (ticketsToTransfer.length === 0) {
      toast.error('Please select at least one ticket to transfer');
      return;
    }

    setTransferring(participant.user_id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/draws/${draw.id}/transfer-tickets`,
        {
          target_draw_id: targetDrawId,
          user_id: participant.user_id,
          ticket_numbers: ticketsToTransfer
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setSelectedTargetDraw(prev => ({ ...prev, [participant.user_id]: '' }));
      setSelectedTickets(prev => ({ ...prev, [participant.user_id]: [] }));
      setShowDropdown(null);
      
      // Refresh the participants list
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Transfer error:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer tickets');
    } finally {
      setTransferring(null);
    }
  };

  const canTransfer = draw.status === 'pending' && availableDraws.length > 0;

  const ticketChips = (participant) => {
    const userSelectedTickets = selectedTickets[participant.user_id] || [];
    const allSelected = userSelectedTickets.length === participant.ticket_numbers.length && participant.ticket_numbers.length > 0;
    return (
      <div>
        <div className="flex flex-wrap items-center gap-1.5">
          {canTransfer && participant.ticket_numbers.length > 1 && (
            <button
              onClick={() => selectAllTickets(participant.user_id, participant.ticket_numbers)}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                allSelected ? 'border-[#A855F7] bg-[#A855F7] text-white' : 'border-white/[0.12] text-[#a1a1aa] hover:border-[#A855F7] hover:text-[#A855F7]'
              }`}
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
          )}
          {participant.ticket_numbers.map((number) => {
            const isSelected = userSelectedTickets.includes(number);
            return (
              <button
                key={number}
                onClick={() => canTransfer && toggleTicketSelection(participant.user_id, number)}
                className={`tabular relative rounded-lg px-2.5 py-1 font-heading text-sm font-semibold transition-all ${canTransfer ? 'tap-none cursor-pointer' : 'cursor-default'} ${
                  isSelected ? 'bg-[#A855F7] text-white ring-2 ring-[#A855F7]/60 ring-offset-2 ring-offset-[#18181b]' : 'bg-white/[0.06] text-[#EAB308] hover:bg-white/[0.1]'
                }`}
                data-testid={`ticket-${participant.user_id}-${number}`}
              >
                #{number}
                {isSelected && <Check className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-[#A855F7] p-0.5" />}
              </button>
            );
          })}
        </div>
        {canTransfer && userSelectedTickets.length > 0 && (
          <div className="mt-2 text-xs text-[#A855F7]">{userSelectedTickets.length} ticket(s) selected</div>
        )}
      </div>
    );
  };

  const transferControls = (participant) => {
    const userSelectedTickets = selectedTickets[participant.user_id] || [];
    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <button
            onClick={() => setShowDropdown(showDropdown === participant.user_id ? null : participant.user_id)}
            className="flex h-10 w-full items-center justify-between gap-2 rounded-xl border border-white/[0.08] bg-[#09090b] px-3 text-sm text-white transition-colors hover:border-[#EAB308] sm:min-w-[160px]"
            data-testid={`transfer-select-${participant.user_id}`}
          >
            <span className="truncate">
              {selectedTargetDraw[participant.user_id]
                ? availableDraws.find((d) => d.id === selectedTargetDraw[participant.user_id])?.title || 'Select draw'
                : 'Select draw'}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-[#a1a1aa]" />
          </button>
          {showDropdown === participant.user_id && (
            <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-white/[0.08] bg-[#09090b] shadow-lg">
              {availableDraws.map((d) => (
                <button
                  key={d.id}
                  onClick={() => { setSelectedTargetDraw((prev) => ({ ...prev, [participant.user_id]: d.id })); setShowDropdown(null); }}
                  className="w-full px-3 py-2 text-left text-sm text-white transition-colors hover:bg-white/[0.06]"
                >
                  <div className="truncate font-medium">{d.title}</div>
                  <div className="text-xs text-[#a1a1aa]">{d.prize_description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <Button
          onClick={() => handleTransfer(participant)}
          disabled={!selectedTargetDraw[participant.user_id] || userSelectedTickets.length === 0 || transferring === participant.user_id}
          size="sm"
          className="h-10 rounded-full bg-[#A855F7] text-white hover:bg-[#9333EA] disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`transfer-btn-${participant.user_id}`}
        >
          {transferring === participant.user_id ? 'Transferring…' : (
            <>
              <ArrowRightLeft className="mr-1 h-4 w-4" />
              Transfer {userSelectedTickets.length > 0 ? `(${userSelectedTickets.length})` : ''}
            </>
          )}
        </Button>
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center sm:p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 36 }}
          onClick={(e) => e.stopPropagation()}
          className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-3xl border border-white/[0.08] bg-[#18181b] sm:max-h-[85vh] sm:max-w-5xl sm:rounded-3xl"
          data-testid="participants-modal"
        >
          <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#06b6d4]/15">
                <Users className="h-5 w-5 text-[#06b6d4]" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate font-heading text-base font-semibold text-white">{draw.title}</h2>
                <p className="truncate text-xs text-[#a1a1aa]">
                  {participants.length} participant{participants.length === 1 ? '' : 's'}{canTransfer && ' · tap tickets to select, then transfer'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="close-participants-modal-btn"
              className="tap-none flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.05] text-[#a1a1aa] transition-colors hover:text-white"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pb-safe">
            {participants.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="mx-auto mb-4 h-12 w-12 text-[#52525b]" />
                <p className="text-[#a1a1aa]">No participants yet</p>
              </div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="divide-y divide-white/[0.06] md:hidden">
                  {participants.map((participant) => (
                    <div key={participant.id} className="space-y-3 p-4" data-testid={`participant-card-${participant.id}`}>
                      <div>
                        <p className="text-[15px] font-semibold text-white">{participant.user?.name}</p>
                        <p className="text-xs text-[#a1a1aa]">{participant.user?.whatsapp_number}</p>
                      </div>
                      {ticketChips(participant)}
                      {canTransfer && transferControls(participant)}
                    </div>
                  ))}
                </div>

                {/* Desktop table */}
                <table className="hidden w-full md:table">
                  <thead className="sticky top-0 bg-[#1f1f23]">
                    <tr>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">Name</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">WhatsApp</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">
                        Ticket Numbers {canTransfer && <span className="text-[#EAB308]">(click to select)</span>}
                      </th>
                      {canTransfer && <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">Transfer</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06]">
                    {participants.map((participant) => (
                      <tr key={participant.id} className="transition-colors hover:bg-white/[0.02]" data-testid={`participant-row-${participant.id}`}>
                        <td className="px-6 py-4 font-medium text-white">{participant.user?.name}</td>
                        <td className="px-6 py-4 text-[#a1a1aa]">{participant.user?.whatsapp_number}</td>
                        <td className="px-6 py-4">{ticketChips(participant)}</td>
                        {canTransfer && <td className="px-6 py-4">{transferControls(participant)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ParticipantsTable;
